/** Зомби, боссы, волны. */
import { ContentId, I18nKey, PosInt, Ticks, UInt, ZombieKind } from "../common";
import { z } from "zod";

/** Обычный зомби. Характеристики — на волне 1; рост по волнам — {@link WaveGenDef}. */
export const ZombieDef = z.object({
  kind: ZombieKind,
  name: I18nKey,
  hp: PosInt,
  /** Скорость, единиц за тик. */
  speed: PosInt,
  /** Урон штабу за удар. */
  damage: PosInt,
  attackCooldownTicks: PosInt,
  /** Щит: поглощает урон до разрушения (только `shield`). */
  shieldHp: UInt.default(0),
  /** Монет за убийство. */
  coins: UInt,
  /** С какой волны появляется. */
  minWave: PosInt,
  /** Вес в составе волны. */
  weight: PosInt,
});
export type ZombieDef = z.infer<typeof ZombieDef>;

/** Особое действие босса — реализует E1. */
export const BossAbility = z.enum(["summon", "charge", "shieldAura"]);
export type BossAbility = z.infer<typeof BossAbility>;

/** Босс: каждая 5-я волна, три босса по очереди. */
export const BossDef = z.object({
  id: ContentId,
  name: I18nKey,
  hp: PosInt,
  speed: PosInt,
  damage: PosInt,
  attackCooldownTicks: PosInt,
  ability: BossAbility,
  abilityCooldownTicks: PosInt,
  params: z.record(z.string(), z.number().int()),
  coins: UInt,
});
export type BossDef = z.infer<typeof BossDef>;

/** Генератор волн (E1). */
export const WaveGenDef = z.object({
  /** Каждая N-я волна — босс (5). */
  bossEvery: PosInt,
  /** Порядок боссов по кругу. */
  bossOrder: z.array(ContentId).min(1),
  /** Целевая длительность волны, тиков: [мин, макс] (20–40 с). */
  targetTicks: z.tuple([PosInt, PosInt]),
  /** Зомби в волне: `base + perWave · (wave − 1)`. */
  count: z.object({
    base: PosInt,
    perWave: UInt,
    /** Потолок числа обычных зомби в волне (нет — без потолка): дальше сила растёт через hp, а не через толпу. */
    max: PosInt.optional(),
  }),
  /** Рост hp зомби за волну, промилле. */
  hpGrowthPermille: UInt,
  /** Мягкий рост hp в поздних волнах (нет — `hpGrowthPermille` на всех волнах): с `fromWave` приращение за волну — `growthPermille`. */
  hpSoft: z.object({ fromWave: PosInt, growthPermille: UInt }).optional(),
  /** Рост hp первых волн, промилле, по порядку с волны 1 (нет — `hpGrowthPermille` с первой волны). */
  hpEarlyPermille: z.array(PosInt).optional(),
  /**
   * Усталость забежки (нет — выключена): каждый стак усилений сверх `freeStacks` добавляет волне `permillePerStack`
   * прочности сверх роста по волне (`compound` — множитель на каждый стак, как растёт и стопка). Держит «снежный ком» стопки усилений в непрерывной забежке.
   */
  runFatigue: z.object({ freeStacks: UInt, permillePerStack: UInt, compound: z.boolean().optional() }).optional(),
  /** Волна 1 новичка: плотные пачки с верхнего и нижнего края, сразу в кадре телефона (нет — обычная волна). */
  opening: z
    .object({
      /** Зомби в волне 1 вместо формулы `count`. */
      count: PosInt,
      /** Отсчёт перед волной 1 вместо `breakTicks`, тиков. */
      introTicks: Ticks,
      /** Зомби в пачке. */
      packSize: PosInt,
      /** Между пачками, тиков. */
      packGapTicks: PosInt,
    })
    .optional(),
  /**
   * Бета: уровни из подволн (нет — обычный режим). Волна движка = подволна: уровень `L` — волны `(L−1)·subwaves+1…L·subwaves`,
   * «1 из 3» и откат — по границам уровня, локация меняется по кругу `locations` (по `locationEveryLevels` уровней каждая).
   * Босс — последняя подволна каждого `bossEveryLevels`-го уровня (`bossEvery` = `subwaves · bossEveryLevels`).
   */
  levels: z
    .object({
      subwaves: PosInt,
      bossEveryLevels: PosInt,
      locationEveryLevels: PosInt,
      locations: z.array(z.string().regex(/^[a-z][a-z0-9_]{1,19}$/)).min(1),
      /** Пауза между подволнами, тиков (~3 с). */
      subwaveBreakTicks: Ticks,
    })
    .optional(),
  /** Пауза между волнами (окно выбора усиления не в счёт), тиков. */
  breakTicks: Ticks,
  /**
   * Пауза перед волной босса вместо `breakTicks`, тиков: под полную перекличку героев на 3 с (ISSUE-145). Нет — старое
   * правило (журналы прежних версий): `breakTicks`.
   */
  bossBreakTicks: Ticks.optional(),
  /** При проигрыше откат на столько волн (1). */
  defeatRollback: UInt,
  /**
   * Откат не ниже волны после пройденного босса (по лучшей волне игрока), каскада нет. Нет — старое правило (журналы
   * прежних версий): только `defeatRollback`.
   */
  bossCheckpoint: z.boolean().optional(),
  /**
   * Открытый герой и его платформа остаются после отката ниже волны открытия: состав забежки уже прошёл проверку
   * открытия. Нет — старое правило (журналы прежних версий): бой сверяет только номер волны.
   */
  keepOpenedHeroes: z.boolean().optional(),
  /** С какой волны доступны «Автонавыки» (10). */
  autoSkillsFromWave: PosInt,
});
export type WaveGenDef = z.infer<typeof WaveGenDef>;
